import React, { useMemo, useEffect, Fragment, useCallback } from 'react'
import day from 'dayjs'
import ParticipantInfo from 'pages/Participants/components/ParticipantInfo'
import ParticipantFormSectionTitle from 'pages/Participants/components/ParticipantFormSectionTitle'
import { CheckinRowItem } from 'pages/CheckIn/model/checkin'
import flowRight from 'lodash/flowRight'
import upperFirst from 'lodash/upperFirst'
import PersonalInfoIcon from 'assets/icons/personal-info.svg'
import InvalidIcon from 'assets/icons/invalid.svg'
import ParticipantAvatar from 'pages/Participants/components/ParticipantAvatar'
import styled from 'styled-components'
import Box from 'shared/components/Box'
import { roleSelector, organizationSettingSelector, permissionSelector } from 'shared/hocs/withAuth'
import { useSelector, useDispatch } from 'react-redux'
import useScreeningActions, { PARTICIPANT_DIALOG } from 'shared/components/Screening/useScreeningActions'
import useMutation from 'shared/hooks/useMutation'
import { Screening, UserScreening, showDialog, hideDialog } from 'shared/redux/app/reducer'
import useQuery from 'shared/hooks/useQuery'
import ConfirmDialog from 'shared/components/ConfirmDialog'
import withDialog, { WrappedProps } from 'shared/hocs/withDialog'
import { CheckInFieldsType } from 'pages/CheckIn/model/checkinField'
import { CheckOutFieldsType } from 'pages/CheckIn/model/checkoutField'
import axios from 'shared/utils/axios'
import pick from 'lodash/pick'
import Loader from 'shared/components/Loader'
import { TypedSession } from 'pages/ProgramsEvents/model/session'
import ViewAllNotesDialog from 'pages/CheckIn/components/Dialogs/ViewAllNotes'
import NoteList from 'shared/components/Notes/components/NoteList'
import ErrorTriangleIcon from 'assets/icons/ErrorTriangle'
import CircularWarningIcon from 'assets/icons/CircularWarning'
import EpiPenIcon from 'assets/icons/EpiPen'
import MedicineIcon from 'assets/icons/Medicine'
import useNoteActions from 'shared/components/Notes/useNoteActions'
import { FLAG_WARNING_COLOR, FLAG_DANGER_COLOR } from 'shared/constants/participant'
import { NoteTypeWithStatus } from 'shared/components/Notes/models/note'
import CustomTooltip from 'shared/components/CustomTooltip'
import PersonWalkingIcon from 'assets/icons/PersonWalking'
import DefaultAvatar from 'assets/images/user_placeholder.png'
import ProtectedImage from 'shared/components/ProtectedImage'
import PhoneIcon from 'assets/icons/phone.svg'
import { ContactType } from 'pages/Participants/model/contact'
import DataTable from 'shared/components/DataTable'
import Checkbox from 'shared/components/Checkbox'
import SelectionControlGroup from 'shared/components/SelectionGroupControl'

type Props = WrappedProps<CheckOutFieldsType> & {
  pageTitle: string
  participant: CheckinRowItem
  showCheckButton?: boolean
  refetch?: () => void
  autohideTimeout: number
}

const TYPES_CLASSNAME: any = {
  event: 'event',
  dropin: 'dropin',
  partner: 'partner',
  registered: 'registered',
}

type PickUpUser = {
  id: string
  photo_url: string
  name: string
  contact_number: string
  relationship: string
}

function CheckOutDetails(props: Props) {
  const { participant, refetch, formState, formHandlers, autohideTimeout = 0 } = props
  const { fields, errors } = formState
  const { onElementChange, onChange } = formHandlers
  const { isAdmin, isOrganization, isCoach, isKiosk } = useSelector(roleSelector)
  const { organization_information } = useSelector(organizationSettingSelector)
  const { CAN_SIGN_WAIVER_FOR_PARTICIPANT } = useSelector(permissionSelector)
  const dispatch = useDispatch()
  const participant_id = useMemo(() => participant?.participant_id || participant?.id, [participant])
  const canSignWaiver = useMemo(() => CAN_SIGN_WAIVER_FOR_PARTICIPANT || isAdmin || isOrganization, [
    isAdmin,
    isOrganization,
  ])
  const fullName = [participant.first_name, participant.last_name].join(' ')
  const { handleShowFollowupDialog, handleClickInitialConfirmation } = useScreeningActions()
  const [{ loading: screeningLoading }, onFetchScreeningusers] = useMutation({
    method: 'POST',
    url: `/note/user/daily/screening`,
    isBase: false,
  })

  const [updateState, onMutate] = useMutation({
    method: 'POST',
    url: `/participant/verify/vaccination`,
    isBase: false,
    message: '',
  })

  const [{ loading, data: participantData }, queryHandler] = useQuery<CheckinRowItem>(
    { url: `/participant/${participant_id}` },
    { initialData: participant, isBase: false, skip: !participant_id },
  )

  const { onMemberUnFlag } = useNoteActions({
    user_id: participant_id,
    hasAdminPermission: true,
    refetch: refetch,
  })

  const handleSignWaiver = async () => {
    const token = await axios.request({
      url: `/user-detail/waiver-token`,
    })
    if (token) {
      const origin = window.location.origin
      const other: any = window.open(`${origin}/sign-waiver/${participant_id}?token=${token}`)

      other.onbeforeunload = () => {
        queryHandler?.refetch?.()
        refetch?.()
      }
    }
  }
  const hasAdminPermission = useMemo(() => isAdmin || isOrganization || isCoach, [isAdmin, isOrganization, isCoach])
  const stats = useMemo(
    () => ({
      ...pick(participant, ['is_waiver_signed', 'is_flagged', 'is_warned', 'hasfilled', 'vaccinated']),
      ...pick(participantData, ['is_waiver_signed', 'is_flagged', 'is_warned', 'hasfilled', 'vaccinated']),
    }),
    [participant, participantData],
  )

  const hasFilledScreening = useMemo(() => {
    return organization_information.is_daily_screening_form && !stats?.is_flagged && stats?.hasfilled
  }, [organization_information.is_daily_screening_form, stats])

  const hasPassedScreening = useMemo(() => {
    return !stats?.is_flagged
  }, [stats])

  const pickUpContacts = useMemo(() => {
    let arr: ContactType[] = []
    if (participantData?.pickup_contacts && participantData.pickup_contacts.length > 0) {
      arr = participantData.pickup_contacts.filter((contact: ContactType) => contact?.can_pickup)
    }
    return arr
  }, [participantData])

  const flaggedNote = useMemo(() => {
    let note = null
    if (participantData?.flags && participantData?.flags?.count > 0) {
      note = participantData.flags.data.find(note => {
        return note.is_flagged
      })
    }
    return note
  }, [participantData])

  const warnedNote = useMemo(() => {
    let note = null
    if (participantData?.flags && participantData?.flags?.count > 0) {
      note = participantData.flags.data.find(note => {
        return note.is_warned
      })
    }
    return note
  }, [participantData])

  useEffect(() => {
    if (
      participantData.id &&
      participantData?.pick_up_permission == 'Child may travel to and from the facility independently'
    )
      formHandlers.onSetForm({
        pick_up_name: 'NA',
        pick_up_contact_number: 'NA',
        pick_up_relationship: 'NA',
      })
  }, [participantData])

  // useEffect(() => {
  //   formHandlers.onSetForm({
  //     isCheckedInAlready: false,
  //     isAllowed: false,
  //   })
  // }, [participant_id])

  // useEffect(() => {
  //   if (organization_information.is_daily_screening_form) {
  //     if (!stats?.is_flagged && stats?.is_waiver_signed && stats?.hasfilled) {
  //       formHandlers.onSetForm({
  //         isCheckedInAlready: false,
  //         isAllowed: true,
  //       })
  //     } else {
  //       formHandlers.onSetForm({
  //         isCheckedInAlready: false,
  //         isAllowed: false,
  //       })
  //     }
  //   } else {
  //     if (stats?.is_waiver_signed && !stats?.is_flagged) {
  //       formHandlers.onSetForm({
  //         isCheckedInAlready: false,
  //         isAllowed: true,
  //       })
  //     } else {
  //       formHandlers.onSetForm({
  //         isCheckedInAlready: false,
  //         isAllowed: false,
  //       })
  //     }
  //   }
  // }, [participant, participantData, stats])

  const openAllNotes = () => {
    dispatch(
      showDialog({
        component: ViewAllNotesDialog,
        props: {
          participant: participantData,
          hasCancel: false,
          hasContinue: false,
          onContinue: () => dispatch(hideDialog()),
        },
      }),
    )
  }

  const onClickMarkAsResolved = useCallback(
    (note: NoteTypeWithStatus) => {
      onMemberUnFlag({ onSuccess: refetch, type: note?.type == 'Manual Warn Flag' ? 'warn' : 'stop' })
    },
    [onMemberUnFlag],
  )

  const getProgramEventType = useCallback((programEvent: TypedSession) => {
    return TYPES_CLASSNAME[programEvent?.type || 'event']
  }, [])

  const pickUpUsers = useMemo(() => {
    const pickUpArr: PickUpUser[] = []
    if (participantData?.pick_up_permission != 'Child may travel to and from the facility independently') {
      if (participantData?.parent) {
        pickUpArr.push({
          id: participantData?.parent.full_name || '',
          photo_url: participantData?.parent.photo_url || '',
          name: participantData?.parent.full_name || '',
          contact_number: participantData?.parent.phone_number || '',
          relationship: 'Parent',
        })
      } else {
        pickUpArr.push({
          id: participantData?.contact_name || '',
          photo_url: '',
          name: participantData?.contact_name || '',
          contact_number: participantData?.phone_number || '',
          relationship: 'Primary Contact',
        })
      }

      pickUpContacts.map(contact =>
        pickUpArr.push({
          id: `${contact.first_name} ${contact.last_name}`,
          photo_url: '',
          name: `${contact.first_name} ${contact.last_name}`,
          contact_number: contact.phone_number,
          relationship: contact.relationship,
        }),
      )
    }
    return pickUpArr
  }, [participantData, pickUpContacts])

  const clickPickUp = useCallback(
    (data: PickUpUser) => {
      formHandlers.onChange('pick_up_name', data.name)
      formHandlers.onChange('pick_up_contact_number', data.contact_number)
      formHandlers.onChange('pick_up_relationship', data.relationship)
    },
    [formHandlers],
  )

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    if (
      autohideTimeout &&
      participant.pick_up_permission == 'Child may travel to and from the facility independently'
    ) {
      timeoutId = setTimeout(() => {
        dispatch(hideDialog())
      }, autohideTimeout)
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return (
    <CheckInContainer>
      {stats?.is_flagged && flaggedNote && (
        <FlagContainer>
          <FlagHeaderContainer className="stop">
            <ErrorTriangleIcon fill="#EEEEEE" height="30px" width="30px" />
            <FlagTitle ml="10px">
              {isKiosk ? `Please speak with a staff member.` : `Stop Member at Check-In.`}
            </FlagTitle>
          </FlagHeaderContainer>
          {!isKiosk && (
            <NoteList
              className="flag-note"
              notes={[flaggedNote]}
              user_id={participant.id}
              showAvatar={false}
              hasRowBtn={true}
              rowBtnTitle="Mark As Resolved"
              onClickRowBtn={onClickMarkAsResolved}
            />
          )}
        </FlagContainer>
      )}
      {stats?.is_warned && warnedNote && !isKiosk && (
        <FlagContainer>
          <FlagHeaderContainer className={`warn ${stats?.is_flagged && flaggedNote ? 'no-border-radius' : ''}`}>
            <CircularWarningIcon fill="#EEEEEE" height="30px" width="30px" />
            <FlagTitle ml="10px" className="warn">
              {isKiosk ? `Take Caution` : `Reminder`}
            </FlagTitle>
          </FlagHeaderContainer>
          {!isKiosk && (
            <NoteList
              className="warn-note"
              notes={[warnedNote]}
              user_id={participant.id}
              showAvatar={false}
              hasRowBtn={true}
              rowBtnTitle="Mark As Resolved"
              onClickRowBtn={onClickMarkAsResolved}
            />
          )}
        </FlagContainer>
      )}
      <CheckOutDetailsContainer hasErrors={stats?.is_flagged || stats?.is_warned}>
        {loading && <Loader />}
        {!loading && (
          <>
            <Header>
              <AvatarContainer>
                <ParticipantAvatar row={participantData} alt={fullName} width="100px" height="100px" />
              </AvatarContainer>
              {!isKiosk && (
                <HeaderDetails>
                  <HeaderName>{fullName}</HeaderName>
                  <HeaderMemberId>Member ID: {participantData.member_id}</HeaderMemberId>
                  <HeaderUsername>Username: {participantData.name}</HeaderUsername>
                </HeaderDetails>
              )}
              {isKiosk && (
                <HeaderDetails>
                  <HeaderName>{fullName}</HeaderName>
                  <HeaderMemberId>Member ID: {participantData.member_id}</HeaderMemberId>
                </HeaderDetails>
              )}
            </Header>
            <UserDetails hasErrors={stats?.is_flagged || stats?.is_warned}>
              {participantData?.pick_up_permission != 'Child may travel to and from the facility independently' && (
                <PickUpContainer>
                  <ParticipantFormSectionTitle
                    icon={PersonWalkingIcon}
                    title="Picked Up By"
                    className="permission-header"
                  />

                  {pickUpUsers.map(pickUp => (
                    <PickUpWithCheckBoxContainer key={`${participantData.id}-${pickUp.id}`}>
                      <Box ml="16px" mr="10px">
                        <Checkbox
                          id={`select-${pickUp.id}`}
                          name={`select-${pickUp.id}`}
                          onChange={(e: any) => {
                            clickPickUp(pickUp)
                          }}
                          checked={pickUp.name === fields.pick_up_name}
                        />
                      </Box>

                      <PickUpUserContainer
                        onClick={(e: any) => {
                          e.stopPropagation()
                          clickPickUp(pickUp)
                        }}
                      >
                        <UserDetailsContainer className="user-details">
                          <ImageContainer>
                            <ProtectedImage
                              className="prof-pic"
                              src={pickUp.photo_url || ''}
                              alt={pickUp.name}
                              defaultSrc={DefaultAvatar}
                            />
                          </ImageContainer>
                          <InfoDetail>{pickUp.name}</InfoDetail>
                        </UserDetailsContainer>
                        <UserDetailsContainer>
                          <img className="image-container" src={PhoneIcon} />
                          <InfoDetail>{pickUp.contact_number}</InfoDetail>
                        </UserDetailsContainer>
                      </PickUpUserContainer>
                    </PickUpWithCheckBoxContainer>
                  ))}
                </PickUpContainer>
              )}

              {!isKiosk && (
                <>
                  <ParticipantFormSectionTitle icon={PersonalInfoIcon} title="Personal Info" />
                  <Grid>
                    <ParticipantInfo label="Age" value={day().diff(day(participantData.dob), 'year')} />
                    <ParticipantInfo
                      label="Medical Considerations"
                      value={
                        participantData.medical_conditions ? participantData.medical_conditions.join(', ') : 'None'
                      }
                    />
                    {(participantData.has_epi_pen == 'Yes' || participantData.has_medications == 'Yes') && (
                      <ParticipantInfoContainer>
                        <InfoLabel>Take Note</InfoLabel>
                        <InfoValue>
                          {participantData.has_epi_pen == 'Yes' && (
                            <CustomTooltip
                              className="custom-tt"
                              placement="bottom"
                              title="Member uses an Epipen"
                              icon={<EpiPenIcon height="24px" width="24px" />}
                            />
                          )}
                          {participantData.has_medications == 'Yes' && (
                            <Box ml="10px" display="flex" lineHeight="16px">
                              <CustomTooltip
                                className="custom-tt"
                                placement="bottom"
                                title="Member takes medication"
                                icon={<MedicineIcon height="24px" width="24px" />}
                              />
                            </Box>
                          )}
                        </InfoValue>
                      </ParticipantInfoContainer>
                    )}
                  </Grid>
                </>
              )}
              <ParticipantFormSectionTitle title="Registered to Today's Programs" />
              <ProgramsList>
                {participantData?.programs_and_events?.length == 0 && (
                  <span className="no-program-item">No programs today.</span>
                )}
                {participantData?.programs_and_events?.map((each: TypedSession) => (
                  <div key={each.id} className={`program-item ${getProgramEventType(each)}`}>
                    <span className="time">{`${day(each.start_date).format('h:mm A')} \u2014 ${day(
                      each.end_date,
                    ).format('h:mm A')}`}</span>
                    <span className="program-name">{each.name}</span>
                  </div>
                ))}
              </ProgramsList>

              <ParticipantFormSectionTitle title="Notes" />
              {participantData?.notes && participantData?.notes?.count > 0 && (
                <>
                  <NoteList notes={participantData?.notes.data} user_id={participant.id} />
                  <AllNotes>
                    <span
                      onClick={() => {
                        openAllNotes()
                      }}
                    >
                      Load More...
                    </span>
                  </AllNotes>
                </>
              )}
              {participantData?.notes?.count === 0 && (
                <span className="empty-note">No notes for this participant. </span>
              )}
            </UserDetails>
          </>
        )}
      </CheckOutDetailsContainer>
    </CheckInContainer>
  )
}

const ParticipantInfoContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 16px;
`

const InfoLabel = styled(Box)`
  font-weight: bold;
  line-height: 19px;
  margin-bottom: 10px;
`

const InfoValue = styled(Box)`
  line-height: 16px;
  display: flex;
`

const PickUpContainer = styled(Box)`
  box-sizing: border-box;
  // min-height: 120px;
  border-radius: 5px;
  margin-top: 20px;
  margin-bottom: 40px;
  padding: 40px 20px;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.main};
  flex-direction: column;
  align-items: flex-start;
`

const PickUpWithCheckBoxContainer = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-content: center;
  border-radius: 8px;
  border: #f9f9f9 1px solid;
  box-shadow: 0 0 1px #e0e0e0;
  background: #f9f9f9;
  margin-top: 3px;
  margin-bottom: 5px;
  cursor: pointer;
  align-items: center;

  &:hover {
    box-shadow: 1px 1px 1px 1px #e0e0e0;
    box-shadow: 0 0 1px #949494;
    border: #949494 1px solid;
  }
`

const PickUpUserContainer = styled(Box)`
  display: flex;
  padding: 20px 0;
`

const UserDetailsContainer = styled(Box)`
  display: flex;
  align-items: center;

  &.user-details {
    margin-right: 20px;
    min-width: 300px;
  }

  .image-container {
    margin-right: 20px;
  }

  .prof-pic {
    border-radius: 5px;
  }
`

const ImageContainer = styled(Box)`
  max-width: 50px;
  margin-right: 20px;
`

const InfoDetail = styled(Box)`
  font-size: 16px;
  line-height: 19px;
  // margin-bottom: 4px;
`

const CheckOutDetailsDialog = flowRight(withDialog())(CheckOutDetails)

const CheckInContainer = styled(Box)`
  overflow: auto;
`

const FlagContainer = styled(Box)`
  display: flex;
  flex-direction: column;

  .flag-note {
    .note-card-wrapper {
      border: none;
    }
  }
  .warn-note {
    .note-card-wrapper {
      border: none;
    }
  }
`

const FlagHeaderContainer = styled(Box)`
  display: flex;
  padding: 10px 30px;
  flex-direction: row;
  width: 100%;
  align-items: center;
  border-radius: 20px 20px 0 0;

  &.stop {
    background: ${FLAG_DANGER_COLOR};
  }
  &.warn {
    background: ${FLAG_WARNING_COLOR};
  }
  &.no-border-radius {
    border-radius: 0;
  }
`

const FlagTitle = styled(Box)`
  color: ${({ theme }) => `${theme.colors.white}`};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;

  &.warn {
    color: ${({ theme }) => `${theme.colors.black}`};
  }
`

const CheckOutDetailsContainer = styled(Box)<{ hasErrors?: boolean }>`
  overflow: auto;
  margin: 20px 15px 0 30px;
  padding-right: 15px;
  // max-height: calc(100vh - 180px);

  ${({ hasErrors }) => `
   ${hasErrors ? 'max-height: calc(100vh - 480px)' : ''}
  `}
`

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 20px;
  row-gap: 30px;
  margin-bottom: 40px;
`

const Header = styled(Box)`
  box-sizing: border-box;
  min-height: 120px;
  border-radius: 5px;
  margin: 0 auto;
  display: flex;
  align-items: center;
`

const ProgramsList = styled(Box)`
  margin-bottom: 20px;
  .no-program-item {
    margin-left: 20px;
    margin-bottom: 10px;
  }

  .program-item {
    box-sizing: border-box;
    min-height: 54px;
    margin: 10px auto;
    display: flex;
    align-items: center;
    background: #fdfdfd;
    border: 1px solid #f9f9f9;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.5);
    border-radius: 8px;

    :nth-child(even) {
      border-left: 5px solid #194583;
    }
    :nth-child(odd) {
      border-left: 5px solid #238064;
    }

    &.event {
      border-left: 5px solid #454545;
    }

    &.dropin {
      border-left: 5px solid #891869;
    }

    &.partner {
      border-left: 5px solid #238064;
    }

    &.registered {
      border-left: 5px solid #194583;
    }
  }

  .time {
    margin: auto 10px;
    color: #727272;
    font-size: 16px;
  }
  .program-name {
    font-weight: bold;
    font-size: 18px;
  }
`

const HeaderDetails = styled(Box)<{ $indicator?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-left: 30px;

  ${({ $indicator }) =>
    $indicator &&
    `
    margin-left: auto;
    margin-right: 30px;
  `};
`

const HeaderName = styled.span`
  font-size: 32px;
  font-weight: bold;
  line-height: 38px;
  margin-bottom: 4px;
`

const HeaderMemberId = styled.span`
  font-size: 16px;
  font-weight: bold;
  line-height: 19px;
  margin-bottom: 4px;
`

const HeaderIndicator = styled.span<{ $warning?: boolean; $action?: boolean; $chknBtn?: boolean }>`
  font-size: 16px;
  font-weight: bold !important;
  line-height: 19px;
  margin-bottom:4px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }

  ${({ $warning, theme }) =>
    $warning
      ? `
        color: ${theme.colors.caution};
      `
      : `
        color: #188C46;
      `}

  ${({ $action }) =>
    $action &&
    `
      text-decoration: underline;
      cursor: pointer;
    `}

  ${({ $chknBtn, theme }) =>
    $chknBtn &&
    `
      margin-top: 12px;
      color: ${theme.colors.secondary};
      display: flex;
      align-items: center;

      .spinner {
        width: 15px;
        height: 15px;
        margin-right: 5px;
      }
    `}
`

const HeaderUsername = styled.span`
  font-size: 16px;
  line-height: 19px;
`

const AvatarContainer = styled(Box)`
  width: 100px;
  height: 100px;

  img {
    width: 100%;
    height: 100%;
  }
`

const UserDetails = styled(Box)<{ hasErrors?: boolean }>`
  // padding-top: 20px;
  margin-bottom: 10px;
  overflow: auto;
  ${({ hasErrors }) => `${hasErrors ? '' : 'max-height: calc(100vh - 380px);'}`}

  .empty-note {
    margin-left: 20px;
    margin-bottom: 10px;
  }
`

const ErrorMessageIcon = styled(Box)`
  display: flex;
  align-items: center;
  margin-right: 10px;
  height: 18px;

  img {
    max-height: 30px;
    height: 30px;
  }
`

export const ErrorMessage = styled(Box)`
  box-sizing: border-box;
  min-height: 48px;
  padding: 5px;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.caution};
  border-radius: 5px;
  background-color: #faebeb;
  margin: 0 auto;
  display: flex;
  align-items: center;
  margin: 8px 0;
  justify-content: space-around;
  align-items: center;
  font-weight: bold;

  .alert-message {
    margin-left: 8px;
  }

  .alert-action {
    min-width: 160px;
    margin-left: 40px;

    ${HeaderIndicator} {
      font-weight: 400;
    }
  }
`

const AllNotes = styled(Box)<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    text-decoration-line: underline;
    margin-top: 32px;

    ${({ disabled, theme }) => `
      color:  ${disabled ? theme.colors.lightGrey : theme.colors.main};
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
    `}
  }
`

CheckOutDetailsDialog.defaultProps = {
  dialogClassName: 'dialog--large',
  hasCancel: false,
  continueLabel: 'Done',
  footerClassName: 'dialog__footer--less-padding',
  continueIsDisabled(fields: CheckOutFieldsType) {
    return !fields?.pick_up_name
  },
}

export default CheckOutDetailsDialog
